import { useEffect, useState } from "react";
import { useSession, signOut } from "../lib/auth-client";
import { SignInPanel } from "../components/admin/SignInPanel";
import { fetchAdminSurvey, type RawResponse, type InitiativeTally, type SurveyStats, type RoomQuestion } from "../lib/survey-aggregate-api";

type Data = { stats: SurveyStats; tallies: InitiativeTally[]; responses: RawResponse[]; questions: RoomQuestion[] };
type Tab = "responses" | "questions";

// One question + the answer to it, flattened across all responses (no per-person
// grouping, no identity). The summary is just the Q&A, searchable.
type QA = { question: string; answer: string };

export default function AdminSurvey() {
  const session = useSession();
  const [data, setData] = useState<Data | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "forbidden" | "error">("idle");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<Tab>("responses");

  useEffect(() => {
    if (!session.data) return;
    setState("loading");
    void fetchAdminSurvey().then((r) => {
      if (r.ok) {
        setData({ stats: r.stats, tallies: r.tallies, responses: r.responses, questions: r.questions });
        setState("idle");
      } else {
        // 401 (no valid session) and 403 (signed in, not allowlisted) both surface
        // the "not an admin" panel; anything else is a transient error.
        setState(r.status === 401 || r.status === 403 ? "forbidden" : "error");
      }
    });
  }, [session.data]);

  if (session.isPending) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-ink-500">Loading…</p>
      </main>
    );
  }

  if (!session.data) {
    return (
      <main className="min-h-screen">
        <SignInPanel />
      </main>
    );
  }

  const allQA: QA[] = data
    ? data.responses.flatMap((r) => r.answers.map((a) => ({ question: a.questionText, answer: a.answerText })))
    : [];
  const needle = query.trim().toLowerCase();
  const filteredQA = needle
    ? allQA.filter((qa) => `${qa.question} ${qa.answer}`.toLowerCase().includes(needle))
    : allQA;

  return (
    <main className="min-h-screen px-6 py-10 max-w-3xl mx-auto">
      <header className="flex items-center justify-between mb-10">
        <h1 className="font-display text-3xl text-ink-900">Survey results</h1>
        <div className="flex items-center gap-4">
          <span className="text-sm text-ink-500">{session.data.user.email}</span>
          <button
            onClick={async () => {
              await signOut();
              window.location.reload();
            }}
            className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-700 transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      {state === "loading" && <p className="text-sm text-ink-500">Loading results…</p>}
      {state === "forbidden" && (
        <div className="rounded-xl border border-parchment-300 bg-parchment-100 p-6">
          <p className="text-ink-900 font-medium mb-1">Signed in, but not an admin</p>
          <p className="text-sm text-ink-500">
            Your account is not on the admin allowlist. Add your user id to ADMIN_USER_IDS to view responses.
          </p>
        </div>
      )}
      {state === "error" && <p className="text-sm text-ink-700">Could not load results. Try again.</p>}

      {data && (
        <>
          <p className="text-sm text-ink-500 mb-6">
            {data.stats.completed} completed {data.stats.completed === 1 ? "response" : "responses"}
            {data.stats.total > data.stats.completed && ` · ${data.stats.total - data.stats.completed} in progress`}
          </p>

          {/* Tabs */}
          <div className="inline-flex rounded-full border border-parchment-300 bg-parchment-100 p-0.5 mb-8">
            {([
              { id: "responses", label: "Responses" },
              { id: "questions", label: `Questions asked${data.questions.length ? ` (${data.questions.length})` : ""}` },
            ] as { id: Tab; label: string }[]).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  aria-pressed={active}
                  className={[
                    "px-4 py-1.5 rounded-full text-sm font-medium transition-colors",
                    active ? "bg-ink-900 text-parchment-50" : "text-ink-500 hover:text-ink-900",
                  ].join(" ")}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === "responses" && (
            <section className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl border border-parchment-300 bg-parchment-100 px-4 py-2">
                <SearchIcon />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search questions and answers for a word…"
                  className="flex-1 bg-transparent py-1.5 text-ink-900 placeholder:text-ink-300/80 focus:outline-none"
                />
                {query && (
                  <button onClick={() => setQuery("")} className="text-xs text-ink-300 hover:text-ink-500">
                    Clear
                  </button>
                )}
              </div>

              {allQA.length === 0 ? (
                <p className="text-sm text-ink-500">No responses yet.</p>
              ) : (
                <>
                  {query && (
                    <p className="text-xs text-ink-300">
                      {filteredQA.length} of {allQA.length} {filteredQA.length === 1 ? "answer mentions" : "answers mention"} "{query}"
                    </p>
                  )}
                  {filteredQA.map((qa, i) => (
                    <div key={i} className="rounded-xl border border-parchment-300 bg-parchment-100 px-4 py-3">
                      <p className="text-xs text-ink-500 mb-1">
                        <Highlight text={qa.question} q={query} />
                      </p>
                      <p className="text-sm text-ink-900 leading-relaxed">
                        <Highlight text={qa.answer} q={query} />
                      </p>
                    </div>
                  ))}
                </>
              )}
            </section>
          )}

          {tab === "questions" && (
            <section className="space-y-2">
              <h2 className="font-display text-xl text-ink-900 mb-1">What the room is asking</h2>
              <p className="text-xs text-ink-300 mb-4">
                Anonymous questions from Our Voices, grouped by wording and ranked by how often they
                were asked.
              </p>
              {data.questions.length === 0 ? (
                <p className="text-sm text-ink-500">No questions asked yet.</p>
              ) : (
                <ol className="space-y-2">
                  {data.questions.map((q, i) => (
                    <li
                      key={i}
                      className="flex items-baseline justify-between gap-4 rounded-xl border border-parchment-300 bg-parchment-100 px-4 py-3"
                    >
                      <span className="text-sm text-ink-900 leading-snug">{q.question}</span>
                      <span className="text-[11px] uppercase tracking-[0.14em] text-ink-300 shrink-0 tabular-nums">
                        {q.count}&times;
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}
        </>
      )}
    </main>
  );
}

function Highlight({ text, q }: { text: string; q?: string }) {
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  if (!lower.includes(needle)) return <>{text}</>;
  const out: React.ReactNode[] = [];
  let i = 0;
  let n = 0;
  while (true) {
    const j = lower.indexOf(needle, i);
    if (j === -1) {
      out.push(text.slice(i));
      break;
    }
    if (j > i) out.push(text.slice(i, j));
    out.push(
      <mark key={n++} className="bg-accent-600/25 text-ink-900 rounded px-0.5">
        {text.slice(j, j + q.length)}
      </mark>,
    );
    i = j + q.length;
  }
  return <>{out}</>;
}

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="text-ink-300" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
